import React, { useEffect, useState } from 'react';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { DateTime } from 'luxon';

import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import Button from '@material-ui/core/Button';
import Tooltip from '@material-ui/core/Tooltip';
import Card from '@material-ui/core/Card';
import CardActions from '@material-ui/core/CardActions';
import CardContent from '@material-ui/core/CardContent';

import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';

import useMediaQuery from '@material-ui/core/useMediaQuery';

import FileTypes from 'app/utils/FileTypes';
import * as Icons from '@material-ui/icons'

import DownloadIcon from '@material-ui/icons/CloudDownload';

import Loading from 'app/components/loaders/Loading';

import { paper } from 'app/styles/core';
import { container } from 'app/styles/new-layout';

import useApi from 'app/context/useApi';

const useStyles = makeStyles(theme => ({
    container: {
        ...container(theme),
        padding: theme.spacing(3),
    },
    paper: {
        ...paper(theme),
        position: 'relative',
        width: '100%'
    },
    tableWrapper: {
        overflowX: 'auto',
    },
    tableCellHead: {
        whiteSpace: "nowrap",
        paddingRight: '32px'
    },
    tableCellHeadEdit: {
        whiteSpace: "nowrap",
        paddingRight: '10px'
    },
    noDocuments: {
        paddingTop: theme.spacing(5),
        paddingBottom: theme.spacing(5),
        textAlign: 'center'
    },
    tableCell: {
        whiteSpace: 'nowrap',
        userSelect: 'none',
        cursor: 'default'
    },
    rowIcon: {
        marginRight: theme.spacing(3),
        verticalAlign: 'middle',
        display: 'inline-block',
        lineHeight: 'normal',
        color: theme.palette.text.secondary
    },
    rowName: {
        verticalAlign: 'middle',
        display: 'inline-block'
    },
}));

export default function Drive(props) {

    const classes = useStyles();
    const api = useApi();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [ files, setFiles ] = useState([]);
    const [ loading, setLoading ] = useState(false);

    const getFiles = () => {
        setLoading(true);

        api.get(`/file`)
            .then((res) => setFiles(res.files))
            .finally(() => setLoading(false));
    }

    const getDownloadLink = id => {
        setLoading(true);

        api.get(`/file/` + id).finally(() => setLoading(false));
    }

    useEffect(() => {
        getFiles();
    }, []);

    const getIcon = file => {

        const fileType = FileTypes.find(obj => obj.ext === file.extension);
        const Icon = Icons[fileType.icon ?? 'Description'];

        return <Icon />
    };

    return (
        !isMobile ? (
            <div className={classes.container}>
                <Paper className={classes.paper} elevation={4}>
                    <Loading isLoading={loading}/>
                    <Grid container
                        alignItems="center"
                        justify="space-between"
                        spacing={3}
                        >
                        <Grid item>
                            <Typography variant="h6" component="h3" gutterBottom>
                                Drive
                            </Typography>
                        </Grid>
                    </Grid>
                    <Typography color="textSecondary" gutterBottom>
                        Your drive contains files that have been made available for download. If you are not seeing a file, check with an administrator to see if you have access.
                    </Typography>
                    <div className={classes.tableWrapper}>
                        {(files.length > 0) ? (
                            <Table className={classes.table}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell className={classes.tableCellHead}>
                                            File Name
                                        </TableCell>
                                        <TableCell className={classes.tableCellHead}>
                                            Date Created
                                        </TableCell>
                                        <TableCell className={classes.tableCellHead}>
                                            Size
                                        </TableCell>
                                        <TableCell align="right" className={classes.tableCellHead}>
                                            Download
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {files.map(file => {
                                        return (
                                            <TableRow hover key={file.id}>
                                                <TableCell
                                                    component="th"
                                                    scope="row"
                                                    className={classes.tableCell}
                                                    >
                                                    <span className={classes.rowIcon}>
                                                        {getIcon(file)}
                                                    </span>
                                                    <Typography
                                                        variant="subtitle2"
                                                        className={classes.rowName}
                                                        >
                                                        {file.name}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell className={classes.tableCell}>
                                                    <Typography variant="subtitle2" color="textSecondary">
                                                        {DateTime.fromISO(file.created_at).toLocaleString(DateTime.DATE_MED)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell className={classes.tableCell}>
                                                    <Typography variant="subtitle2" color="textSecondary">
                                                        {file.size_formatted}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell
                                                    className={classes.tableCellEdit}
                                                    align="right"
                                                    >
                                                    <Tooltip title="Download" disableFocusListener>
                                                        <IconButton
                                                            aria-label="Download"
                                                            onClick={() => getDownloadLink(file.id)}
                                                            >
                                                            <DownloadIcon fontSize="small"/>
                                                        </IconButton>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        )
                                    })}
                                </TableBody>
                            </Table>
                        ) : (
                            <div className={classes.noDocuments}>
                                <Typography variant="h6" color="textSecondary">
                                    Currently you have no files available to download.
                                </Typography>
                            </div>
                        )}
                    </div>
                </Paper>
            </div>
        ) : (
            <div className={classes.container}>
                <Grid container direction="column" spacing={2}>
                    <Grid item>
                        <Paper className={classes.paper} elevation={4}>
                            <Loading isLoading={loading}/>
                            <Grid container
                                alignItems="center"
                                justify="space-between"
                                spacing={3}
                                >
                                <Grid item>
                                    <Typography variant="h6" component="h3" gutterBottom>
                                        Drive
                                    </Typography>
                                </Grid>
                            </Grid>
                            <Typography color="textSecondary" gutterBottom>
                                Your drive contains files that have been made available for download. If you are not seeing a file, check with an administrator to see if you have access.
                            </Typography>
                        </Paper>
                    </Grid>
                    {files.map(file => 
                        <Grid item key={file.id}>
                            <Card className={classes.root} >
                                <CardContent>
                                <Typography variant="h6" component="h2" gutterBottom>
                                    {file.name}
                                </Typography>
                                <Typography className={classes.title} color="textSecondary" gutterBottom>
                                    <strong>Uploaded On:</strong>  {DateTime.fromISO(file.created_at).toLocaleString(DateTime.DATE_MED)}
                                </Typography>
                                <Typography className={classes.pos} color="textSecondary">
                                    <strong>Size:</strong>  {file.size_formatted}
                                </Typography>
                                </CardContent>
                                <CardActions>
                                    <Grid container justify="flex-end">
                                        <Grid item>
                                            <Button 
                                                color="primary"
                                                onClick={() => getDownloadLink(file.id)}
                                                variant="outlined"
                                                startIcon={<DownloadIcon/>}
                                            >
                                                Download
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </CardActions>
                            </Card>
                        </Grid>
                    )}
                </Grid>
            </div>
        )
    );
}
